<template>
  <button :type="type" :disabled="isDisabled" :class="computedClasses" class="BaseButton">
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center BaseButton__spinner"
      :class="{ 'BaseButton__spinner--secondary': variant === 'secondary' }"
    >
      <svg
        class="animate-spin h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>

    <span :class="{ 'opacity-0': loading, 'opacity-100': !loading }">
      <slot></slot>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    width?: string
    height?: string
    variant?: 'primary' | 'secondary'
  }>(),
  {
    type: 'button',
    disabled: false,
    loading: false,
    width: 'w-96',
    height: 'h-12',
    variant: 'primary',
  },
)

const isDisabled = computed(() => props.disabled || props.loading)

const variant = computed(() => props.variant)

const computedClasses = computed(() => {
  const base = [
    props.width,
    props.height,
    'font-medium',
    'px-4',
    'rounded-xl',
    'transition-colors',
    'relative',
    'disabled:cursor-not-allowed',
  ]
  if (props.variant === 'secondary') {
    return [...base, 'cursor-pointer', 'BaseButton--secondary', 'disabled:opacity-50']
  }
  return [
    ...base,
    'cursor-pointer',
    'bg-main',
    'hover:bg-main-hover',
    'text-white',
    'disabled:bg-gray-300',
    'disabled:text-gray-500',
    'disabled:hover:bg-gray-300',
  ]
})
</script>

<style scoped>
.BaseButton__spinner {
  color: white;
}

.BaseButton__spinner--secondary {
  color: var(--color-main);
}

.BaseButton--secondary {
  background-color: transparent;
  color: var(--color-main);
  border: 1px solid var(--color-main);
}

.BaseButton--secondary:hover:not(:disabled) {
  background-color: rgba(var(--v-theme-main-blue), 0.08);
}

.BaseButton--secondary:disabled {
  border-color: rgba(0, 0, 0, 0.2);
  color: rgba(0, 0, 0, 0.4);
}

.BaseButton:disabled {
  cursor: not-allowed;
}
</style>
